import { Fragment, useEffect, useRef } from 'react'

import { useTimeIndicator } from 'features/meetings/hooks'

import { CurrentTimeIndicator } from './CurrentTimeIndicator'
import { DateBadge } from './date_badge'
import { EmptyState } from './EmptyState'
import { EventsGroup } from './styles'
import { MeetingCard } from '../../meeting_card'

export const MeetingGroup = ({
  date,
  rawDate,
  events,
  selectedMeetingId,
  showBuffers,
  anyInProgress,
  buffers,
  firstVisibleMIEvent,
  trackFromContactPage = false,
  packageNameMap = {},
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  firstUpcomingEventKey,
  registerDateGroupRef
}) => {
  const groupRef = useRef(null)
  // Use comparable date (YYYY-MM-DD) for time indicator; 'date' may be formatted (e.g. "Monday, December 15, 2025")
  const comparableDate = rawDate || events[0]?.date || date
  const {
    indicatorIndex,
    mostRecentInProgressIndex,
    showIndicatorAfterAll,
    hasRealtimeFeatures
  } = useTimeIndicator(comparableDate, events)

  // rawDate (YYYY-MM-DD) is preferred for useScrollToTodayButton hook element lookup
  const dateForComparison = rawDate || events[0]?.date || null

  useEffect(() => {
    if (registerDateGroupRef && dateForComparison) {
      registerDateGroupRef(dateForComparison, groupRef.current)
      return () => {
        registerDateGroupRef(dateForComparison, null)
      }
    }
    return () => {}
  }, [registerDateGroupRef, dateForComparison])

  return (
    <EventsGroup ref={groupRef} data-date={dateForComparison}>
      <DateBadge date={date} />
      <div>
        {events.map((event, index, list) => {
          const { key } = event
          const isLast = index === list.length - 1
          const isFirst = index === 0
          const hasSpotlight = firstVisibleMIEvent?.key === key
          const showIndicatorBefore = index === indicatorIndex
          const isMostRecentInProgress = index === mostRecentInProgressIndex
          const hasIndicatorAfter = showIndicatorAfterAll && isLast

          return (
            <Fragment key={key}>
              {showIndicatorBefore && (
                <CurrentTimeIndicator isRealtime={hasRealtimeFeatures} />
              )}
              {event.type === 'NO_TODAY_MEETINGS' || event.type === 'NO_FUTURE_MEETINGS' ? (
                <EmptyState type={event.type} />
              ) : (
                <MeetingCard
                  anyInProgress={anyInProgress}
                  buffers={buffers}
                  event={event}
                  hasIndicatorAfter={hasIndicatorAfter}
                  hasIndicatorBefore={showIndicatorBefore}
                  hasSpotlight={hasSpotlight}
                  isFirst={isFirst}
                  isLast={isLast}
                  isMostRecentInProgress={isMostRecentInProgress}
                  isNext={showIndicatorBefore}
                  otherEventsOnDay={list.filter(({ uuid: otherKey }) => otherKey !== key)}
                  selectedEventId={selectedMeetingId}
                  showBuffers={showBuffers}
                  trackFromContactPage={trackFromContactPage}
                  packageName={packageNameMap[event.uuid]}
                  onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback}
                  onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback}
                  isFirstUpcomingForTour={(event.key ?? event.uuid) === firstUpcomingEventKey}
                />
              )}
            </Fragment>
          )
        })}
        {showIndicatorAfterAll && (
          <CurrentTimeIndicator isRealtime={hasRealtimeFeatures} />
        )}
      </div>
    </EventsGroup>
  )
}
